"use client";

import { c as _c } from "react/compiler-runtime";
import type { Route } from "next";
import Link from "next/link";
import { useParams, useRouter } from "next/navigation";
import type { MouseEvent } from "react";
import { memo, useCallback, useMemo } from "react";
import { ProjectStatusBadge } from "@/components/projects/project-status-badge";
import { TableSkeleton } from "@/components/skeleton/table-skeleton";
import { TableBodyRowEmpty } from "@/components/table-body-row-empty";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow, tableRowActionClassName } from "@/components/ui/table";
import { agriculturalActivitylist, livestockActivitylist } from "@/data/entity-types";
import { dateFormat } from "@/lib/date-format";
import { getProjectDetailsDefaultSearch } from "@/lib/router-search-defaults";
import { cn } from "@/lib/utils";
import type { useProjectsData } from "../hooks/use-projects-data";
type ProjectsDataReturn = ReturnType<typeof useProjectsData>;
type ProjectTableItem = ProjectsDataReturn["projects"][number];
type ProjectsTableMode = "operating_cost" | "investment";
const TableHeaderComponent = memo(function TableHeaderComponentMemo(t0) {
  const $ = _c(12);
  const {
    mode
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <TableHead>Código</TableHead>;
    t2 = <TableHead>Produtor</TableHead>;
    t3 = <TableHead>Enquadramento</TableHead>;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] !== mode) {
    t4 = mode === "investment" ? <TableHead>Programa</TableHead> : <><TableHead>Cultura/Atividade</TableHead><TableHead>Safra</TableHead></>;
    $[3] = mode;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <TableHead>Data última atualização</TableHead>;
    t6 = <TableHead>Status</TableHead>;
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  let t7;
  if ($[7] !== mode) {
    t7 = mode === "investment" ? <TableHead className="text-right">Ações</TableHead> : null;
    $[7] = mode;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t4 || $[10] !== t7) {
    t8 = <TableHeader className="sticky top-0 z-10"><TableRow>{t1}{t2}{t3}{t4}{t5}{t6}{t7}</TableRow></TableHeader>;
    $[9] = t4;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  return t8;
});
const ProjectTableRow = memo(function ProjectTableRowMemo(t0) {
  const $ = _c(59);
  const {
    project,
    projectHref,
    mode
  } = t0;
  const router = useRouter();
  const projectTypeCategory = project.projectTypeCode ?? "agricultural";
  project.activity?.title;
  let t1;
  bb0: {
    if (project.activity?.title) {
      t1 = project.activity.title;
      break bb0;
    }
    if (!project.activityId) {
      t1 = "Atividade n\xE3o definida";
      break bb0;
    }
    if (projectTypeCategory === "agricultural") {
      let t2;
      if ($[0] !== project.activityId) {
        t2 = agriculturalActivitylist.find(i => i.id === project.activityId);
        $[0] = project.activityId;
        $[1] = t2;
      } else {
        t2 = $[1];
      }
      const agriculturalActivityItem = t2;
      t1 = agriculturalActivityItem?.title ?? "Atividade indispon\xEDvel";
      break bb0;
    }
    let t2;
    if ($[2] !== project.activityId) {
      t2 = livestockActivitylist.find(i_0 => i_0.id === project.activityId);
      $[2] = project.activityId;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    const livestockActivityItem = t2;
    t1 = livestockActivityItem?.title ?? "Atividade indispon\xEDvel";
  }
  const activityItemTitle = t1;
  let t2;
  bb1: {
    const producer = project.producer;
    if (!producer) {
      t2 = "Produtor n\xE3o definido";
      break bb1;
    }
    t2 = producer.documentType === "cpf" ? producer.name ?? "Produtor sem nome" : producer.companyName ?? "Produtor sem nome";
  }
  const producerName = t2;
  let t3;
  if ($[4] !== project.producer?.primaryAddress) {
    t3 = () => {
      const address = project.producer?.primaryAddress;
      if (!address) {
        return;
      }
      const parts = [address.city, address.state].filter(Boolean).join(" / ");
      return parts.length > 0 ? parts : undefined;
    };
    $[4] = project.producer?.primaryAddress;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  project.producer?.primaryAddress;
  let t4;
  if ($[6] !== t3) {
    t4 = t3();
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const producerLocation = t4;
  let t5;
  if ($[8] !== project.updatedAt) {
    const updatedAt = new Date(project.updatedAt);
    t5 = dateFormat(updatedAt);
    $[8] = project.updatedAt;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const formattedDate = t5;
  let t6;
  if ($[10] !== projectHref || $[11] !== router) {
    t6 = () => {
      router.push(projectHref);
    };
    $[10] = projectHref;
    $[11] = router;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const openProject = t6;
  let t7;
  if ($[13] !== openProject) {
    t7 = event => {
      if ((event.target as HTMLElement).closest("a, button")) {
        return;
      }
      openProject();
    };
    $[13] = openProject;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const handleRowClick = t7;
  let t8;
  if ($[15] !== projectHref || $[16] !== router) {
    t8 = () => {
      router.prefetch(projectHref);
    };
    $[15] = projectHref;
    $[16] = router;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const handleRowMouseEnter = t8;
  let t9;
  if ($[18] !== openProject) {
    t9 = event_0 => {
      event_0.stopPropagation();
      openProject();
    };
    $[18] = openProject;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const handleOpenButtonClick = t9;
  const t10 = `project-row-${project._id}`;
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = cn(tableRowActionClassName, "block");
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== project.projectCode || $[22] !== projectHref) {
    t12 = <TableCell><Link href={projectHref} className={t11}>{project.projectCode}</Link></TableCell>;
    $[21] = project.projectCode;
    $[22] = projectHref;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== producerName) {
    t13 = <span className="text-foreground font-medium">{producerName}</span>;
    $[24] = producerName;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== producerLocation) {
    t14 = producerLocation ? <span className="text-muted-foreground">{producerLocation}</span> : null;
    $[26] = producerLocation;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t13 || $[29] !== t14) {
    t15 = <TableCell><div className="flex flex-col">{t13}{t14}</div></TableCell>;
    $[28] = t13;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  const t16 = project.producerClassification?.title ?? project.financingFramework?.title ?? "Enquadramento indefinido";
  let t17;
  if ($[31] !== t16) {
    t17 = <TableCell><Badge variant="secondary">{t16}</Badge></TableCell>;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== activityItemTitle || $[34] !== mode || $[35] !== project.investmentProgram?.title) {
    t18 = <TableCell>{mode === "investment" ? <span>{project.investmentProgram?.title ?? "Programa n\xE3o informado"}</span> : <span>{activityItemTitle}</span>}</TableCell>;
    $[33] = activityItemTitle;
    $[34] = mode;
    $[35] = project.investmentProgram?.title;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== mode || $[38] !== project.harvest) {
    t19 = mode === "investment" ? null : <TableCell><span>{project.harvest ?? "Safra n\xE3o informada"}</span></TableCell>;
    $[37] = mode;
    $[38] = project.harvest;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== formattedDate) {
    t20 = <TableCell><span className="text-muted-foreground tabular-nums">{formattedDate}</span></TableCell>;
    $[40] = formattedDate;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== project.status) {
    t21 = <TableCell><ProjectStatusBadge status={project.status} /></TableCell>;
    $[42] = project.status;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== handleOpenButtonClick || $[45] !== mode) {
    t22 = mode === "investment" ? <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={handleOpenButtonClick}>Abrir</Button></TableCell> : null;
    $[44] = handleOpenButtonClick;
    $[45] = mode;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== handleRowClick || $[48] !== handleRowMouseEnter || $[49] !== t10 || $[50] !== t12 || $[51] !== t15 || $[52] !== t17 || $[53] !== t18 || $[54] !== t19 || $[55] !== t20 || $[56] !== t21 || $[57] !== t22) {
    t23 = <TableRow className="cursor-pointer" data-testid={t10} onClick={handleRowClick} onMouseEnter={handleRowMouseEnter}>{t12}{t15}{t17}{t18}{t19}{t20}{t21}{t22}</TableRow>;
    $[47] = handleRowClick;
    $[48] = handleRowMouseEnter;
    $[49] = t10;
    $[50] = t12;
    $[51] = t15;
    $[52] = t17;
    $[53] = t18;
    $[54] = t19;
    $[55] = t20;
    $[56] = t21;
    $[57] = t22;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  return t23;
});
export const ProjectsTable = memo(function ProjectsTableComponent(t0) {
  const $ = _c(35);
  const {
    data,
    isLoading,
    isStale: t1,
    mode: t2
  } = t0;
  const isStale = t1 === undefined ? false : t1;
  const mode = t2 === undefined ? "operating_cost" : t2;
  const params = useParams();
  const organizationSlug = params.organizationSlug;
  let t3;
  if ($[0] !== mode || $[1] !== organizationSlug) {
    t3 = project => {
      const searchParams = new URLSearchParams();
      const technicalProjectType = project.technicalProjectType ?? mode;
      for (const [key, value] of Object.entries(getProjectDetailsDefaultSearch(technicalProjectType))) {
        searchParams.set(key, String(value));
      }
      return `/${organizationSlug}/projects/${project._id}?${searchParams.toString()}` as Route;
    };
    $[0] = mode;
    $[1] = organizationSlug;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const getProjectHref = t3;
  let t4;
  if ($[3] !== getProjectHref || $[4] !== mode) {
    t4 = project_0 => <ProjectTableRow key={project_0._id} mode={mode} project={project_0} projectHref={getProjectHref(project_0)} />;
    $[3] = getProjectHref;
    $[4] = mode;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const renderRow = t4;
  mode === "investment" ? 7 : 7;
  if (isLoading) {
    let t5;
    if ($[6] !== mode) {
      t5 = <TableHeaderComponent mode={mode} />;
      $[6] = mode;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    let t6;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <TableBody><TableSkeleton columns={7} rows={8} /></TableBody>;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    let t7;
    if ($[9] !== t5) {
      t7 = <Table>{t5}{t6}</Table>;
      $[9] = t5;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    let t8;
    if ($[11] !== isStale || $[12] !== t7) {
      t8 = <div className="space-y-6"><TableContainer isStale={isStale}>{t7}</TableContainer></div>;
      $[11] = isStale;
      $[12] = t7;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    return t8;
  }
  if (!data.length) {
    let t5;
    if ($[14] !== mode) {
      t5 = <TableHeaderComponent mode={mode} />;
      $[14] = mode;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    let t6;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <TableBody><TableBodyRowEmpty columns={7} message="Nenhum projeto encontrado" /></TableBody>;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    let t7;
    if ($[17] !== t5) {
      t7 = <Table>{t5}{t6}</Table>;
      $[17] = t5;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    let t8;
    if ($[19] !== isStale || $[20] !== t7) {
      t8 = <div className="space-y-6"><TableContainer isStale={isStale}>{t7}</TableContainer></div>;
      $[19] = isStale;
      $[20] = t7;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    return t8;
  }
  let t5;
  if ($[22] !== mode) {
    t5 = <TableHeaderComponent mode={mode} />;
    $[22] = mode;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== data || $[25] !== renderRow) {
    t6 = data.map(renderRow);
    $[24] = data;
    $[25] = renderRow;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  let t7;
  if ($[27] !== t6) {
    t7 = <TableBody>{t6}</TableBody>;
    $[27] = t6;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  let t8;
  if ($[29] !== t5 || $[30] !== t7) {
    t8 = <Table>{t5}{t7}</Table>;
    $[29] = t5;
    $[30] = t7;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== isStale || $[33] !== t8) {
    t9 = <div className="space-y-6"><TableContainer isStale={isStale}>{t8}</TableContainer></div>;
    $[32] = isStale;
    $[33] = t8;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  return t9;
});